import { createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';
import { toast } from '@/components/ui/use-toast';
import { i18next } from '@/lib/i18n/adapters/i18next';
import { queryClient } from '@/lib/queryClient';
import { OAUTH_PROFILE_ID_KEY, govProfileKey } from './-query/key';
import { handleOAuthCallback } from './profile.$profileId/-query/api';

// 検索パラメータのスキーマ
const searchSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
  session_state: z.string().optional(),
  error: z.string().optional(),
  error_description: z.string().optional()
});
export const Route = createFileRoute('/_staff/labor-core/settings/egov-callback')({
  validateSearch: zodValidator(searchSchema),
  loaderDeps: ({
    search
  }) => ({
    search
  }),
  loader: async ({
    deps: {
      search
    }
  }) => {
    /**
     * loaderはReact外で実行されるため、i18nextを直接使用
     * i18next.t の型定義はstrictモードで翻訳キーを厳密にチェックするが、
     * ここでは動的キーを使用するため型チェックをバイパス
     */
    const t = (key: string): string => {
      const fullKey = `pages.laborCore.settings.callback.${key}`;
      return (i18next as any).t(fullKey);
    };

    // sessionStorageからprofileIdを取得
    const profileId = sessionStorage.getItem(OAUTH_PROFILE_ID_KEY);

    // profileIdがない場合はエラー
    if (!profileId) {
      toast({
        variant: 'destructive',
        description: t('sessionNotFound')
      });
      throw redirect({
        to: '/labor-core/settings',
        hash: 'applicant-contacts'
      });
    }

    // sessionStorageをクリア
    sessionStorage.removeItem(OAUTH_PROFILE_ID_KEY);

    // e-Govからエラーが返された場合
    if (search.error) {
      toast({
        variant: 'destructive',
        description: search.error_description || search.error
      });
      throw redirect({
        to: '/labor-core/settings/profile/$profileId',
        params: {
          profileId
        },
        hash: 'egov-account'
      });
    }

    // codeまたはstateがない場合
    if (!search.code || !search.state) {
      toast({
        variant: 'destructive',
        description: t('invalidAuthInfo')
      });
      throw redirect({
        to: '/labor-core/settings/profile/$profileId',
        params: {
          profileId
        },
        hash: 'egov-account'
      });
    }

    // バックエンドにcodeを送信
    try {
      await handleOAuthCallback(profileId, {
        code: search.code,
        state: search.state
      });

      // OAuthステータスのキャッシュを無効化
      await queryClient.invalidateQueries({
        queryKey: govProfileKey.oauthStatus(profileId)
      });
      toast({
        description: t('connectionComplete')
      });
    } catch {
      toast({
        variant: 'destructive',
        description: t('connectionFailed')
      });
    }

    // プロフィール詳細ページにリダイレクト
    throw redirect({
      to: '/labor-core/settings/profile/$profileId',
      params: {
        profileId
      },
      hash: 'egov-account'
    });
  },
  component: () => null // loaderでリダイレクトするためコンポーネントは不要
});